import React from 'react';
import { Box, Typography } from '@mui/material';
import { marketingPalette } from '../marketingSystem';

/**
 * Canonical Ormal flow — real-time optimization, super-idealized.
 *
 *   Payment intents  →  Ormal Optimization  →  Optimized stack
 *
 * Left: live payment intents arriving from the merchant's checkout, sub, or
 *   order system. Each row has ticket, geography, surface, buyer class —
 *   the signals the optimization conditions on.
 *
 * Middle: Ormal Optimization. The priority weights (cost / conversion /
 *   risk) plus the one-line description of what the models actually do —
 *   score every route for every intent against those weights, in real time.
 *
 * Right: the optimized stack. A coordinated composition of method archetypes,
 *   each tuned to a different buyer segment — not a ranking, not a shortlist.
 *   Abstracted at the category level (wallet / card / financing / local rails)
 *   so we don't collapse into "here are three specific methods."
 */

const paymentIntents = [
  { amount: '$120 USD', context: 'US mobile · new buyer · low basket', accent: '#0f6fff' },
  { amount: '£240 GBP', context: 'UK desktop · returning · mid basket', accent: '#0ea5e9' },
  { amount: '$612 USD', context: 'US desktop · higher ticket', accent: '#6366f1' },
  { amount: '€89 EUR', context: 'DE mobile · cross-border', accent: '#8b5cf6' },
];

const stackSlots = [
  {
    role: 'Lead · Wallet',
    method: 'Wallet path',
    note: 'Served first to wallet-capable buyers — the cleanest approval path on mobile.',
  },
  {
    role: 'Core · Card',
    method: 'Tokenized card path',
    note: 'Main path for non-wallet buyers, with tokenization uplift on returning card use.',
  },
  {
    role: 'Alt · Financing',
    method: 'Financing path',
    note: 'Surfaced when the basket crosses the financing threshold — catches completion that card alone leaves on the table.',
  },
  {
    role: 'Local · Cross-border',
    method: 'Local-rails path',
    note: 'Injected when geography differs from billing — local familiarity raises approval on unfamiliar checkouts.',
  },
];

const priorityWeights = [
  { label: 'Cost', weight: 25, tint: '#0b57d0' },
  { label: 'Conversion', weight: 50, tint: '#047857' },
  { label: 'Risk', weight: 25, tint: '#7c3aed' },
];

const columnCardSx = {
  p: { xs: 1.8, md: 2.05 },
  borderRadius: 1.4,
  border: `1px solid ${marketingPalette.border}`,
  background:
    'linear-gradient(180deg, rgba(255,255,255,0.985) 0%, rgba(250,252,255,0.985) 100%)',
  boxShadow: '0 18px 38px rgba(15,23,42,0.055)',
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 1.25,
};

const overlineSx = {
  fontSize: '0.66rem',
  fontWeight: 400,
  letterSpacing: '0.1em',
  textTransform: 'uppercase' as const,
  color: marketingPalette.muted,
};

const ArrowConnector: React.FC<{ label?: string }> = ({ label }) => (
  <Box
    aria-hidden
    sx={{
      display: { xs: 'none', md: 'flex' },
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      color: marketingPalette.accent,
      gap: 0.5,
      minWidth: 32,
      px: 0.5,
    }}
  >
    {label && (
      <Typography sx={{ fontSize: '0.58rem', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase', color: marketingPalette.muted }}>
        {label}
      </Typography>
    )}
    <svg width="32" height="16" viewBox="0 0 32 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M0 8 H26" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M22 3 L30 8 L22 13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  </Box>
);

const VerticalArrow: React.FC = () => (
  <Box
    aria-hidden
    sx={{
      display: { xs: 'flex', md: 'none' },
      justifyContent: 'center',
      color: marketingPalette.accent,
      py: 0.6,
    }}
  >
    <svg width="14" height="22" viewBox="0 0 14 22" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M7 0 V18" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <path d="M2 14 L7 20 L12 14" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  </Box>
);

const ConductorCore: React.FC = () => (
  <Box
    sx={{
      ...columnCardSx,
      background:
        'linear-gradient(180deg, #10264d 0%, #0a1932 100%)',
      border: '1px solid rgba(125,211,252,0.28)',
      boxShadow: '0 32px 60px rgba(10,24,64,0.38), 0 0 0 1px rgba(15,111,255,0.18)',
      color: 'white',
      gap: 1.35,
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        borderRadius: 'inherit',
        background:
          'radial-gradient(circle at 80% 6%, rgba(125,211,252,0.22), transparent 42%), radial-gradient(circle at 10% 95%, rgba(96,165,250,0.18), transparent 46%)',
        pointerEvents: 'none',
      },
    }}
  >
    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box
        sx={{
          display: 'inline-grid',
          gridTemplateColumns: 'repeat(3, 7px)',
          gap: '4px',
          alignItems: 'end',
          height: 22,
        }}
      >
        {[14, 22, 18].map((h, i) => (
          <Box
            key={i}
            sx={{
              width: 7,
              height: h,
              borderRadius: 999,
              background: ['#7dd3fc', '#60a5fa', '#93c5fd'][i],
              boxShadow: '0 0 10px rgba(125,211,252,0.4)',
            }}
          />
        ))}
      </Box>
      <Typography sx={{ fontSize: '0.92rem', fontWeight: 400, letterSpacing: '-0.01em', color: 'white' }}>
        Ormal Optimization
      </Typography>
    </Box>

    <Box sx={{ position: 'relative' }}>
      <Typography sx={{ ...overlineSx, color: 'rgba(191,219,254,0.78)' }}>
        Priority weights
      </Typography>
      <Box sx={{ mt: 0.9, display: 'flex', flexDirection: 'column', gap: 0.65 }}>
        {priorityWeights.map((w) => (
          <Box key={w.label}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.2 }}>
              <Typography sx={{ fontSize: '0.74rem', fontWeight: 400, color: 'rgba(226,232,240,0.92)' }}>
                {w.label}
              </Typography>
              <Typography sx={{ fontSize: '0.74rem', fontWeight: 400, color: 'white', fontVariantNumeric: 'tabular-nums' }}>
                {w.weight}%
              </Typography>
            </Box>
            <Box sx={{ position: 'relative', height: 9, borderRadius: 999, backgroundColor: 'rgba(148,163,184,0.22)' }}>
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: `${w.weight}%`,
                  borderRadius: 999,
                  background: `linear-gradient(90deg, ${w.tint} 0%, ${w.tint}ee 55%, ${w.tint}99 100%)`,
                  boxShadow: `0 0 0 1px ${w.tint}55, 0 0 14px ${w.tint}66`,
                }}
              />
            </Box>
          </Box>
        ))}
      </Box>
    </Box>

    <Box
      sx={{
        position: 'relative',
        p: 1.15,
        borderRadius: 1,
        border: '1px solid rgba(147,197,253,0.2)',
        backgroundColor: 'rgba(15,23,42,0.42)',
      }}
    >
      <Typography sx={{ ...overlineSx, color: 'rgba(191,219,254,0.82)' }}>
        Inside the optimization
      </Typography>
      <Typography sx={{ mt: 0.45, fontSize: '0.76rem', lineHeight: 1.5, color: 'rgba(226,232,240,0.94)', fontWeight: 400 }}>
        Optimization models score every candidate route against the priority weights, per intent, in real time — and assemble the stack that best serves each buyer segment.
      </Typography>
    </Box>
  </Box>
);

export default function CheckoutStackCard() {
  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0,1fr) auto minmax(0,1fr) auto minmax(0,1.2fr)' },
          columnGap: { md: 0.8 },
          rowGap: { xs: 0, md: 0 },
          alignItems: 'stretch',
        }}
      >
        {/* --- Left: Payment intents --- */}
        <Box sx={columnCardSx}>
          <Typography sx={overlineSx}>Payment intents</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {paymentIntents.map((intent) => (
              <Box
                key={intent.amount + intent.context}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.95,
                  p: 0.95,
                  borderRadius: 0.95,
                  border: `1px solid ${marketingPalette.border}`,
                  backgroundColor: '#ffffff',
                }}
              >
                <Box
                  sx={{
                    width: 9,
                    height: 9,
                    borderRadius: '50%',
                    background: intent.accent,
                    flexShrink: 0,
                    boxShadow: `0 0 0 3px ${intent.accent}22`,
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.84rem', fontWeight: 400, color: marketingPalette.headline, lineHeight: 1.25, fontVariantNumeric: 'tabular-nums' }}>
                    {intent.amount}
                  </Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: marketingPalette.muted, lineHeight: 1.35 }}>
                    {intent.context}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
          <Box sx={{ mt: 'auto' }}>
            <Typography sx={{ fontSize: '0.68rem', color: marketingPalette.subtle, lineHeight: 1.5 }}>
              Intents stream from checkout, subscription, or order systems — the ticket, geography, surface, and buyer class are the signals Ormal conditions on.
            </Typography>
          </Box>
        </Box>

        <ArrowConnector label="ingests" />
        <VerticalArrow />

        {/* --- Middle: Ormal Optimization --- */}
        <ConductorCore />

        <ArrowConnector label="assembles" />
        <VerticalArrow />

        {/* --- Right: Optimized stack --- */}
        <Box sx={columnCardSx}>
          <Typography sx={overlineSx}>Optimized stack</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {stackSlots.map((slot, index) => (
              <Box
                key={slot.role}
                sx={{
                  position: 'relative',
                  p: 1,
                  borderRadius: 0.95,
                  border: `1px solid ${index === 0 ? 'rgba(15,111,255,0.34)' : marketingPalette.border}`,
                  backgroundColor: index === 0 ? 'rgba(15,111,255,0.05)' : '#ffffff',
                }}
              >
                <Typography sx={{ fontSize: '0.62rem', fontWeight: 400, letterSpacing: '0.09em', textTransform: 'uppercase', color: marketingPalette.accent }}>
                  {slot.role}
                </Typography>
                <Typography sx={{ mt: 0.22, fontSize: '0.88rem', fontWeight: 400, color: marketingPalette.headline, lineHeight: 1.25 }}>
                  {slot.method}
                </Typography>
                <Typography sx={{ mt: 0.25, fontSize: '0.72rem', lineHeight: 1.45, color: marketingPalette.muted }}>
                  {slot.note}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ mt: 'auto' }}>
            <Typography sx={{ fontSize: '0.68rem', color: marketingPalette.subtle, lineHeight: 1.5 }}>
              The stack is a composition — each path serves a buyer segment the others would leave on the table. Not a ranking. And every composition ships with its reasoning: what it chose, what it rejected, and the numbers that decided it.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
